/**
 * Générateur — Comparer des fractions.
 * Attendu ID : 923 | Niveau : 5ème | Domaine : Nombres et calculs / Nombres rationnels
 *
 * Plan de cartes (mise en conformité avec la spécification 923, campagne 5e,
 * 2026-09-21) :
 *
 *   Carte « Comparer deux fractions » (id comparer)
 *     comparer_qcm_unique          : deux fractions, un symbole parmi <, > et =
 *                                    (niveaux 1, 2, 3 et variante « égales »).
 *     comparer_tableau             : quatre paires, une par niveau 1/2/3 et une
 *                                    paire égale, colonnes <, >, = (variante
 *                                    transverse : l'ordre peut être large).
 *     comparer_droite_texte_trou_saisie : niveau 1, variante « droite graduée »
 *                                    (moitiés, tiers, quarts, dixièmes) : lire
 *                                    deux abscisses, dire laquelle est la plus
 *                                    grande.
 *
 *   Carte « Ranger avec un dénominateur commun » (id denominateur)
 *     denominateur_texte_trou_saisie : deux fractions et un entier réécrits sur
 *                                    le plus petit dénominateur commun (numérateur
 *                                    ET dénominateur à saisir), puis la lettre du
 *                                    plus grand.
 *     denominateur_qcm_unique      : trois fractions et un entier, choisir le
 *                                    rangement croissant juste parmi quatre.
 *     denominateur_ordonner        : niveau 4, quatre fractions et un entier, les
 *                                    trois relations de dénominateurs mêlées, au
 *                                    moins une fraction supérieure à 1.
 *     denominateur_negatifs_ordonner : niveau 5, mêmes rangements, numérateurs de
 *                                    signe quelconque.
 *
 * Spécification (C:\tanoed\Attendu-5e\specifications-tanoed\5e\923.json) :
 *   Bornes appliquées : dénominateurs 2..20, chacun tiré une seule fois par
 *   carte ; numérateurs 1..30 ; valeur absolue ≤ 4 ; plus petit dénominateur
 *   commun ≤ 60 ; nombres à comparer 2 (QCM, tableau, droite), 3 (trous) ou 5
 *   (rangements) ; entier inséré 1..3 ; écart minimal 0,05 entre deux nombres
 *   (sur la valeur absolue de chaque différence, deux à deux) sauf la paire
 *   égale ; droite graduée en 2, 3, 4 ou 10 ; numérateurs négatifs au seul
 *   niveau 5. Le rangement demandé est toujours croissant.
 *   Exclusions : aucune fraction égale à un entier, aucun numérateur nul, aucun
 *   dénominateur 1 parmi les fractions, aucune égalité hors variante « égales ».
 *   Erreurs visées → mots-clés :
 *     comparer les seuls numérateurs                 → numerateurs_seuls
 *     plus grand dénominateur = plus grande fraction → plus_grand_denominateur
 *     toute fraction serait inférieure à 1           → fraction_toujours_inferieure_a_1
 *     l'entier n'est pas vu comme une fraction       → entier_mis_a_part
 *     produit des dénominateurs au lieu du ppcm      → produit_des_denominateurs
 *     dénominateur multiplié, numérateur oublié      → numerateur_non_multiplie
 *     ordre des valeurs absolues gardé (négatifs)    → ordre_des_valeurs_absolues
 *     tiers comptés depuis la première graduation    → comptage_depuis_premiere_graduation
 *   Graduation → cartes : niveau 1 → comparer_qcm_unique (cas 1), comparer_tableau,
 *   comparer_droite_texte_trou_saisie ; niveau 2 → comparer_qcm_unique (cas 2),
 *   comparer_tableau ; niveau 3 → comparer_qcm_unique (cas 3), comparer_tableau,
 *   denominateur_texte_trou_saisie ; niveau 4 → denominateur_ordonner,
 *   denominateur_qcm_unique ; niveau 5 → denominateur_negatifs_ordonner ;
 *   variante « égales » → comparer_qcm_unique, comparer_tableau.
 *   Non tranché : le rangement en « champs reliés par < » du type_reponse est
 *   rendu par le format ordonner (glisser-déposer), seule fabrique existante ;
 *   la carte de réécriture à trous est gardée comme étape du mode non interactif
 *   décrit dans type_reponse. Le troisième symbole d'un QCM à trois entrées ne
 *   sert parfois aucune erreur visée (réponse fermée imposée par la spec).
 *
 * Invariants : comparaison des fractions par produit en croix (jamais de
 * flottant) ; écart minimal testé en entiers (20·|ad − cb| ≥ bd) ; boucles
 * bornées avec repli déterministe vérifié ; distracteurs de QCM tous distincts
 * de la clé et entre eux, sinon nouveau tirage complet.
 */

import {
    randomChoice, randInt, shuffle, pickLettresSains,
    makeQcm, makeTexteTrou, makeOrdonner, basePayload, tex, fracTex,
} from '../../../utils';
import { gcd } from '../../6eme/fractions/fraction';

const ATTENDU_ID = 923;

const L_COMPARER = 'Comparer deux fractions';
const L_DENOMINATEUR = 'Ranger avec un dénominateur commun';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ─── Bornes de la spécification ─────────────────────────────────────────
const DEN_MIN = 2;
const DEN_MAX = 20;
const NUM_MAX = 30;
const VALEUR_MAX = 4;
const PPCM_MAX = 60;
const ENTIER_MIN = 1;
const ENTIER_MAX = 3;
const GRADUATIONS_DROITE = [2, 3, 4, 10];

const lcm = (a, b) => (a * b) / gcd(a, b);
const lcmListe = (liste) => liste.reduce((acc, d) => lcm(acc, d), 1);

/** Relation entre deux dénominateurs : 1 multiples, 2 premiers entre eux, 3 facteur commun sans être multiples. */
const relation = (b, d) => {
    if (b === d) return 0;
    if (b % d === 0 || d % b === 0) return 1;
    if (gcd(b, d) === 1) return 2;
    return 3;
};

/** |a/b − c/d| ≥ 0,05, en entiers : 20·|ad − cb| ≥ bd. */
const ecartSuffisant = (a, b, c, d) => 20 * Math.abs(a * d - c * b) >= b * d;
/** Toutes les différences deux à deux (en valeur absolue) ≥ 0,05. */
const ecartsSuffisants = (fractions) => fractions.every((f, i) =>
    fractions.every((g, j) => j <= i || ecartSuffisant(f.num, f.den, g.num, g.den)));
const compare = (x, y) => (x < y ? '<' : x > y ? '>' : '=');
/** Symbole entre a/b et c/d (produit en croix, dénominateurs > 0). */
const symbole = (a, b, c, d) => compare(a * d, c * b);

/** Numérateur admissible pour den : 1..30, ≤ 4·den, jamais multiple de den. */
const tirerNumerateur = (den) => {
    for (let essai = 0; essai < 40; essai++) {
        const n = randInt(1, Math.min(NUM_MAX, VALEUR_MAX * den));
        if (n % den !== 0) return n;
    }
    return 1;
};
const tirerDenominateur = (exclus = []) => {
    for (let essai = 0; essai < 60; essai++) {
        const d = randInt(DEN_MIN, DEN_MAX);
        if (!exclus.includes(d)) return d;
    }
    return DEN_MIN;
};

/** Affichage KaTeX d'un nombre { num, den } (den 1 = entier). */
const affiche = (f) => (f.den === 1 ? tex(String(f.num)) : fracTex(f.num, f.den));
/** Même écriture sans les `$`, style texte (pour une option de QCM en une seule formule). */
const afficheTex = (f) => {
    if (f.den === 1) return String(f.num);
    return `${f.num < 0 ? '-' : ''}\\frac{${Math.abs(f.num)}}{${f.den}}`;
};

// ═════════ Carte « Comparer deux fractions » ═════════

const CAS_PAIRE = [1, 2, 3, 'egales'];

const REPLI_PAIRE = {
    1: [{ num: 3, den: 4 }, { num: 5, den: 8 }],
    2: [{ num: 2, den: 3 }, { num: 3, den: 5 }],
    3: [{ num: 5, den: 6 }, { num: 7, den: 9 }],
    egales: [{ num: 2, den: 3 }, { num: 4, den: 6 }],
};

/**
 * Deux fractions positives selon le cas : relation des dénominateurs 1, 2 ou 3
 * (ppcm ≤ 60, écart ≥ 0,05, une erreur visée au moins provoquée : la
 * comparaison des numérateurs seuls ou celle des dénominateurs donne un autre
 * symbole), ou « egales » (a/b et ka/kb, kb ≤ 20, ka ≤ 30).
 */
const tirerPaire = (cas, plafond = PPCM_MAX) => {
    for (let essai = 0; essai < 200; essai++) {
        if (cas === 'egales') {
            const b = randInt(DEN_MIN, 10);
            const k = randInt(2, Math.floor(DEN_MAX / b));
            const a = tirerNumerateur(b);
            if (k * a > NUM_MAX) continue;
            return shuffle([{ num: a, den: b }, { num: k * a, den: k * b }]);
        }
        const b = tirerDenominateur();
        const d = tirerDenominateur([b]);
        if (relation(b, d) !== cas || lcm(b, d) > plafond) continue;
        const a = tirerNumerateur(b);
        const c = tirerNumerateur(d);
        if (!ecartSuffisant(a, b, c, d)) continue;
        const juste = symbole(a, b, c, d);
        if (compare(a, c) === juste && compare(b, d) === juste) continue;
        return [{ num: a, den: b }, { num: c, den: d }];
    }
    return REPLI_PAIRE[cas].map(f => ({ ...f }));
};

/** Distracteurs d'une paire : les deux autres symboles, avec l'erreur qu'ils servent. */
const distracteursSymboles = ([f, g]) => {
    const juste = symbole(f.num, f.den, g.num, g.den);
    const parNumerateurs = compare(f.num, g.num);
    const parDenominateurs = compare(f.den, g.den);
    return ['<', '>', '='].filter(s => s !== juste).map(s => {
        const codes = [];
        if (s === parNumerateurs) codes.push('numerateurs_seuls');
        if (s === parDenominateurs) codes.push('plus_grand_denominateur');
        return { valeur: s, code: codes.length ? codes.join('+') : null };
    });
};

/**
 * Le plafond du dénominateur commun sur la carte à RÉPONSES FERMÉES : 30 au lieu des
 * 60 de la spécification (arbitrage prof 2026-09-25).
 *
 * Cette carte demande le symbole directement, sans étape intermédiaire : tout le calcul
 * reste dans la tête. Comparer 21/12 et 26/20 y demandait un dénominateur commun de 60,
 * le maximum autorisé, ce qui n'est pas un travail de tête en début de 5e.
 *
 * Le troisième cas, celui des dénominateurs à facteur commun, reste servi ici avec des
 * paires plus douces, et les paires lourdes continuent de sortir sur les cartes faites
 * pour elles : le tableau et la mise au même dénominateur, où l'élève ÉCRIT les
 * fractions équivalentes avant de conclure.
 */
const PPCM_MAX_QCM = 30;

const genComparerQcm = () => {
    const cas = randomChoice(CAS_PAIRE);
    const paire = tirerPaire(cas, PPCM_MAX_QCM);
    const [f, g] = paire;
    const correct = symbole(f.num, f.den, g.num, g.den);
    const texte = '<p><b>Compare ces deux fractions : choisis le symbole qui convient.</b></p>'
        + `<p>${tex(`\\dfrac{${f.num}}{${f.den}} \\; \\ldots \\; \\dfrac{${g.num}}{${g.den}}`)}</p>`;
    const payload = makeQcm(['<', '>', '='], correct, texte, opts(L_COMPARER));
    payload._meta.params = { niveau: cas, paire, distracteurs: distracteursSymboles(paire) };
    return payload;
};

const genComparerTableau = () => {
    const paires = shuffle(CAS_PAIRE).map(cas => ({ cas, paire: tirerPaire(cas) }));
    const colonnes = ['<', '>', '='];
    const lignes = paires.map(({ paire: [f, g] }) => `${affiche(f)} … ${affiche(g)}`);
    const bonnes = paires.map(({ paire: [f, g] }) => colonnes.indexOf(symbole(f.num, f.den, g.num, g.den)));
    const tableEl = {
        type: 'table',
        config: { type: 'colonnesLibres', colonnes, lignes, bonnesReponses: bonnes },
    };
    const br = lignes.map((l, i) => `[${l}|${colonnes[bonnes[i]]}]`).join('');
    const payload = basePayload('qcm',
        '<p><b>Pour chaque ligne, coche le symbole qui convient :</b> &lt;, &gt; ou =. Deux fractions peuvent être égales.</p>',
        { choix: [], bonne_reponse: br },
        opts(L_COMPARER),
        { version: 1, elements: [tableEl] });
    const distracteurs = paires.flatMap(({ paire }) => distracteursSymboles(paire)).filter(d => d.code);
    payload._meta.params = { paires, distracteurs };
    return payload;
};

// ─── Variante « droite graduée » (niveau 1) ───
const COULEUR_POINT = '#1E3A8A';
const POOL_LETTRES = 'ABCDEFGHKLMNPRST'.split('');

const genComparerDroite = () => {
    const d = randomChoice(GRADUATIONS_DROITE);
    const end = randomChoice([2, 3]);
    const [lA, lB] = pickLettresSains(POOL_LETTRES, 2);
    let iA = 1;
    let iB = 2;
    for (let essai = 0; essai < 60; essai++) {
        const x = randInt(1, end * d - 1);
        const y = randInt(1, end * d - 1);
        if (x % d === 0 || y % d === 0 || x === y) continue;
        iA = x; iB = y;
        break;
    }
    if (iA % d === 0 || iB % d === 0 || iA === iB) { iA = 1; iB = d + 1; }
    const config = {
        start: 0, end, step: 1 / d,
        reperes: Array.from({ length: end - 1 }, (_, k) => k + 1),
        points: [
            { value: iA / d, label: lA, color: COULEUR_POINT },
            { value: iB / d, label: lB, color: COULEUR_POINT },
        ],
        lineType: 'half', showArrow: true, showStart: true, showEnd: true,
    };
    const etai = '\\rule[-0.55em]{0pt}{0.55em}';
    const plusGrande = iA > iB ? lA : lB;
    const phrase = `$x_${lA} = \\dfrac{{{${iA}}}${etai}}{${d}}$\n`
        + `$x_${lB} = \\dfrac{{{${iB}}}${etai}}{${d}}$\n`
        + `$\\text{Le point d'abscisse la plus grande est} \\; {{${plusGrande}}}$`;
    const payload = makeTexteTrou(phrase,
        '<p><b>Lis l\'abscisse de chaque point : complète le numérateur de chaque fraction.</b> '
        + 'Puis écris la lettre du point dont l\'abscisse est la plus grande.</p>',
        { ...opts(L_COMPARER), mode: 'saisie', elements_additionnels: { version: 1, elements: [{ type: 'numberline', config }] } });
    payload._meta.params = {
        niveau: 1, d, end,
        paire: [{ num: iA, den: d }, { num: iB, den: d }],
        erreurVisee: ['comptage_depuis_premiere_graduation'],
    };
    return payload;
};

// ═════════ Carte « Ranger avec un dénominateur commun » ═════════

/** Fraction à deux cases (numérateur puis dénominateur), gabarit 1050. */
const fracDeuxCases = (n, d) => `\\dfrac{{{${n}}}\\rule[-0.55em]{0pt}{0.55em}}{\\rule{0pt}{1.3em}{{${d}}}}`;

/**
 * Deux fractions (relation 1 ou 3 : le produit des dénominateurs n'est pas le
 * plus petit dénominateur commun) et un entier, écarts ≥ 0,05.
 */
const tirerLotTrous = () => {
    for (let essai = 0; essai < 200; essai++) {
        const cas = randomChoice([1, 3]);
        const b = tirerDenominateur();
        const d = tirerDenominateur([b]);
        if (relation(b, d) !== cas) continue;
        const D = lcm(b, d);
        if (D > PPCM_MAX || D === b * d) continue;
        const entier = randInt(ENTIER_MIN, ENTIER_MAX);
        const lot = [{ num: tirerNumerateur(b), den: b }, { num: tirerNumerateur(d), den: d }, { num: entier, den: 1 }];
        if (!ecartsSuffisants(lot)) continue;
        return { D, lot };
    }
    return { D: 12, lot: [{ num: 7, den: 4 }, { num: 5, den: 6 }, { num: 2, den: 1 }] };
};

const LETTRES_LOT = ['A', 'B', 'C'];

const genDenominateurTrous = () => {
    const { D, lot } = tirerLotTrous();
    const convertis = lot.map(f => f.num * (D / f.den));
    const lettreMax = LETTRES_LOT[convertis.indexOf(Math.max(...convertis))];
    const phrase = lot
        .map((f, i) => `$${LETTRES_LOT[i]} = ${f.den === 1 ? f.num : `\\dfrac{${f.num}}{${f.den}}`} = ${fracDeuxCases(convertis[i], D)}$`)
        .join('\n')
        + `\n$\\text{Le plus grand des trois nombres est} \\; {{${lettreMax}}}$`;
    const payload = makeTexteTrou(phrase,
        '<p><b>Écris chaque nombre sous la forme d\'une fraction ayant pour dénominateur le plus petit dénominateur commun :</b> '
        + 'complète le numérateur et le dénominateur. Puis écris la lettre du plus grand nombre : A, B ou C.</p>',
        { ...opts(L_DENOMINATEUR), mode: 'saisie' });
    payload._meta.params = {
        niveau: 3, D, lot, convertis, lettreMax,
        erreurVisee: ['produit_des_denominateurs', 'numerateur_non_multiplie', 'entier_mis_a_part'],
    };
    return payload;
};

/** Les trois relations de dénominateurs sont-elles toutes présentes parmi les paires ? */
const relationsMelees = (dens) => {
    const vues = new Set();
    dens.forEach((b, i) => dens.forEach((d, j) => { if (j > i) vues.add(relation(b, d)); }));
    return [1, 2, 3].every(r => vues.has(r));
};

const trierCroissant = (liste) => [...liste].sort((f, g) => f.num * g.den - g.num * f.den);

/**
 * Rangement : nbFractions fractions de dénominateurs distincts, les trois
 * relations mêlées (si demandé), ppcm ≤ 60, au moins une fraction de valeur
 * absolue > 1, un entier 1..3, écarts ≥ 0,05, signes selon `negatifs`.
 */
const tirerLotRangement = ({ nbFractions, negatifs, melees }) => {
    for (let essai = 0; essai < 300; essai++) {
        const dens = [];
        while (dens.length < nbFractions) dens.push(tirerDenominateur(dens));
        if (lcmListe(dens) > PPCM_MAX) continue;
        if (melees && !relationsMelees(dens)) continue;
        const fractions = dens.map(den => ({ num: tirerNumerateur(den), den }));
        if (!fractions.some(f => f.num > f.den)) continue;
        if (negatifs) {
            fractions.forEach(f => { if (randomChoice([true, false])) f.num = -f.num; });
            if (!fractions.some(f => f.num < 0)) continue;
        }
        const entier = { num: randInt(ENTIER_MIN, ENTIER_MAX), den: 1 };
        const lot = [...fractions, entier];
        if (!ecartsSuffisants(lot)) continue;
        return lot;
    }
    return negatifs
        ? [{ num: -7, den: 4 }, { num: 5, den: 6 }, { num: -1, den: 3 }, { num: 3, den: 5 }, { num: 2, den: 1 }]
        : [{ num: 7, den: 4 }, { num: 5, den: 6 }, { num: 1, den: 3 }, { num: 3, den: 5 }, { num: 2, den: 1 }];
};

const genDenominateurOrdonner = () => {
    const lot = tirerLotRangement({ nbFractions: 4, negatifs: false, melees: true });
    const tries = trierCroissant(lot);
    const payload = makeOrdonner(tries.map(affiche),
        '<p><b>Range ces nombres dans l\'ordre croissant</b>, du plus petit au plus grand.</p>',
        opts(L_DENOMINATEUR));
    payload._meta.params = {
        niveau: 4, lot: tries, D: lcmListe(lot.map(f => f.den)),
        erreurVisee: ['fraction_toujours_inferieure_a_1', 'entier_mis_a_part'],
    };
    return payload;
};

const genDenominateurNegatifsOrdonner = () => {
    const lot = tirerLotRangement({ nbFractions: 4, negatifs: true, melees: true });
    const tries = trierCroissant(lot);
    const payload = makeOrdonner(tries.map(affiche),
        '<p><b>Range ces nombres dans l\'ordre croissant</b>, du plus petit au plus grand. Attention aux signes.</p>',
        opts(L_DENOMINATEUR));
    payload._meta.params = {
        niveau: 5, lot: tries, D: lcmListe(lot.map(f => f.den)),
        erreurVisee: ['ordre_des_valeurs_absolues', 'entier_mis_a_part'],
    };
    return payload;
};

/** Écriture KaTeX d'un rangement croissant en une seule formule. */
const rangementTex = (liste) => tex(liste.map(afficheTex).join(' < '));

/**
 * QCM de rangement : trois fractions et un entier. Les trois distracteurs sont
 * des rangements produits par une erreur visée, tous distincts de la clé et
 * entre eux par construction (sinon nouveau tirage complet).
 */
const tirerQcmRangement = () => {
    for (let essai = 0; essai < 200; essai++) {
        const lot = tirerLotRangement({ nbFractions: 3, negatifs: false, melees: true });
        const justes = trierCroissant(lot);
        const entier = lot.find(f => f.den === 1);
        const fractions = lot.filter(f => f.den !== 1);
        // L'entier n'est pas le plus grand : le mettre à part change le rangement.
        if (justes[justes.length - 1] === entier) continue;
        if (new Set(lot.map(f => f.num)).size !== lot.length) continue;
        const parNumerateurs = [...lot].sort((f, g) => f.num - g.num);
        const parDenominateurs = [...lot].sort((f, g) => f.den - g.den);
        const entierApart = [...trierCroissant(fractions), entier];
        const candidats = [
            { liste: parNumerateurs, code: 'numerateurs_seuls' },
            { liste: parDenominateurs, code: 'plus_grand_denominateur' },
            { liste: entierApart, code: 'entier_mis_a_part' },
        ];
        const textes = new Set([rangementTex(justes)]);
        let ok = true;
        candidats.forEach(c => {
            c.valeur = rangementTex(c.liste);
            if (textes.has(c.valeur)) ok = false;
            textes.add(c.valeur);
        });
        if (!ok) continue;
        return { lot, justes, candidats };
    }
    const lot = [{ num: 7, den: 4 }, { num: 3, den: 5 }, { num: 9, den: 10 }, { num: 1, den: 1 }];
    const justes = trierCroissant(lot);
    const entier = lot[3];
    const candidats = [
        { liste: [...lot].sort((f, g) => f.num - g.num), code: 'numerateurs_seuls' },
        { liste: [...lot].sort((f, g) => f.den - g.den), code: 'plus_grand_denominateur' },
        { liste: [...trierCroissant(lot.slice(0, 3)), entier], code: 'entier_mis_a_part' },
    ].map(c => ({ ...c, valeur: rangementTex(c.liste) }));
    return { lot, justes, candidats };
};

const genDenominateurQcm = () => {
    const { lot, justes, candidats } = tirerQcmRangement();
    const correct = rangementTex(justes);
    const texte = '<p><b>Quel est le rangement de ces quatre nombres dans l\'ordre croissant ?</b></p>'
        + `<p>${shuffle(lot).map(affiche).join(' ; ')}</p>`;
    const payload = makeQcm([correct, ...candidats.map(c => c.valeur)], correct, texte, opts(L_DENOMINATEUR));
    payload._meta.params = {
        niveau: 4, lot: justes, D: lcmListe(lot.map(f => f.den)),
        distracteurs: candidats.map(c => ({ valeur: c.valeur, code: c.code })),
    };
    return payload;
};

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'chaque distracteur porte son code dans _meta.params.distracteurs ; jamais de bouche-trou : collision → nouveau tirage complet',
        'comparaison par produit en croix, écart minimal 0,05 testé en entiers',
    ],
    comparer_qcm_unique: {
        support: 'deux fractions, réponse fermée <, >, = ; la paire est construite pour qu\'au moins un raccourci fautif donne un autre symbole',
        numerateurs_seuls: 'le symbole donné par la comparaison des numérateurs',
        plus_grand_denominateur: 'le symbole donné par la comparaison des dénominateurs',
    },
    denominateur_qcm_unique: {
        support: 'trois fractions et un entier (jamais le plus grand), choisir le rangement croissant',
        numerateurs_seuls: 'rangement par numérateurs croissants',
        plus_grand_denominateur: 'rangement par dénominateurs croissants',
        entier_mis_a_part: 'fractions bien rangées, l\'entier rejeté en dernier',
    },
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    comparer_qcm_unique: genComparerQcm,
    comparer_tableau: genComparerTableau,
    comparer_droite_texte_trou_saisie: genComparerDroite,
    denominateur_texte_trou_saisie: genDenominateurTrous,
    denominateur_qcm_unique: genDenominateurQcm,
    denominateur_ordonner: genDenominateurOrdonner,
    denominateur_negatifs_ordonner: genDenominateurNegatifsOrdonner,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', tableau: 'Tableau', droite_texte_trou_saisie: 'Droite graduée',
    texte_trou_saisie: 'Saisie trous', ordonner: 'Ordonner', negatifs_ordonner: 'Ordonner (négatifs)',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'comparer', label: L_COMPARER, formatsList: buildFormats('comparer') },
    { id: 'denominateur', label: L_DENOMINATEUR, formatsList: buildFormats('denominateur') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
